import Styles from "./formDateField.lit.scss";
import {FormInputElement} from "./formInputElement";
import {customElement, property, query} from "lit/decorators.js";
import {InputElementType} from "../validations/validations";
import {html, type PropertyValues, type TemplateResult} from "lit";
import {LocalDate} from "../../../common/utils/dates/localDate";
import {LocatedDates} from "../../../common/utils/dates/locatedDates";
import {resolve} from "../../../container";
import {spreadAttributes} from "../../../common/directives/spreadAttributes";
import {ValidDate} from "../validations/validDate";
import {Dictionary} from "../../../page/elements/dictionary";
import type {Maybe} from "../../../bootstrap/common/types";

@customElement("eop-form-date-field")
export class EopFormDateField extends FormInputElement<string> {

    public static readonly styles = Styles;

    private locatedDates: LocatedDates = resolve(LocatedDates);
    @property({attribute: "label"})
    public label: string;
    @query("input")
    private input: HTMLInputElement;

    public constructor() {
        super("");
        this.type = InputElementType.DATE;
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.addBasicDateValidation();
    }

    public render(): TemplateResult {
        return html`
            <div class="input-element ${this.basicClassMap()}">
                <label for=${this.formUUId} @click=${this.preventLabelDefaultAction}>
                    <span class="label-text">${this.label}</span>
                    ${this.labelSuffix()}
                </label>
                <input
                        id=${this.formUUId}
                        name=${this.getFullId()}
                        .value=${this.value}
                        ?filled=${this.value}
                        @change=${this.change}
                        @input=${this.onInput}
                        type=${this.type}
                        ?required=${this.required}
                        ${spreadAttributes(this.customAttributes)}
                >
                ${this.renderValidationIcon()}
            </div>
            ${(this.renderValidationMessages())}
        `;
    }

    protected updated(changedProperties: PropertyValues): void {
        super.updated(changedProperties);
        if (changedProperties.has("value")) {
            this.updateValidState(this.value);
            this.requestUpdate();
        }
    }

    public focusInput(): void {
        this.input.focus();
    }

    protected preset(): Maybe<string> {
        return this.getAttribute("preset") ?? undefined;
    }

    protected valueEnhancements(inputValue: string): Map<string, string> {
        const date = LocalDate.fromDateExpression(inputValue).toDate();
        if (date) {
            return new Map([["date", this.locatedDates.toLocalDateString(date)]]);
        } else {
            return new Map();
        }
    }

    private change(event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        this.updateValue(value);
    }

    private onInput(event: Event): void {
        if (this.isValidatable) {
            this.updateValidState((event.target as HTMLInputElement).value);
        }
    }

    private addBasicDateValidation(): void {
        const msg = Dictionary.of(this).translate("BAD_INPUT_FOR_DATE");
        this.validations.push(new ValidDate(msg, () => this.input));
    }
}